/**
 * Version history list. One row per weight_version (newest first) with set_by,
 * cell count, created_at, active badge, and an Activate action for inactive
 * versions. For set_by='learned' rows, a "Check promotion" action fetches the
 * advisory promotion-status and renders it inline (PWE 3.6).
 *
 * Ported from quill-app/quill-admin's VersionHistoryPanel.tsx. The original used
 * a Redux thunk (fetchPromotionStatus) + a slice-held `promotionStatus` map;
 * that state is local to this component now (it's purely a read-through cache
 * for a display-only panel, so there is no reason for it to live in shared
 * state) and calls quillApi.weights.getPromotionStatus directly.
 */

import React from 'react';
import {
  Paper,
  List,
  ListItem,
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  quillApi,
  WeightHistoryRow,
  WeightMatrix,
  PromotionStatus,
  QuillApiError,
} from '../../../services/quillAdminService';
import PromotionStatusPanel from './PromotionStatusPanel';
import WeightMatrixGrid from './WeightMatrixGrid';

const NO_OP = () => {};

interface Props {
  history: WeightHistoryRow[];
  onActivate: (version: number) => void;
}

const setByColor = (setBy: string): 'default' | 'secondary' | 'info' => {
  if (setBy === 'learned') return 'secondary';
  if (setBy === 'admin') return 'info';
  return 'default';
};

const VersionHistoryPanel: React.FC<Props> = ({ history, onActivate }) => {
  const [promotionStatus, setPromotionStatus] = React.useState<Record<number, PromotionStatus>>({});
  const [loadingVersion, setLoadingVersion] = React.useState<number | null>(null);

  // Historical version viewer (quick-win C). Loads a past version's full matrix
  // read-only in a dialog — deliberately isolated from the active-version editor
  // on the page (its own local state, no shared edits touched).
  const [viewVersion, setViewVersion] = React.useState<number | null>(null);
  const [viewMatrix, setViewMatrix] = React.useState<WeightMatrix | null>(null);
  const [viewLoading, setViewLoading] = React.useState(false);
  const [viewError, setViewError] = React.useState<string | null>(null);

  const checkPromotion = async (version: number) => {
    setLoadingVersion(version);
    try {
      const status = await quillApi.weights.getPromotionStatus(version);
      setPromotionStatus((prev) => ({ ...prev, [version]: status }));
    } finally {
      setLoadingVersion(null);
    }
  };

  const viewHistorical = async (version: number) => {
    setViewVersion(version);
    setViewMatrix(null);
    setViewError(null);
    setViewLoading(true);
    try {
      setViewMatrix(await quillApi.weights.getVersion(version));
    } catch (e) {
      setViewError(e instanceof QuillApiError ? e.message : 'Failed to load version.');
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewer = () => {
    setViewVersion(null);
    setViewMatrix(null);
    setViewError(null);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Version history
      </Typography>
      <List disablePadding>
        {history.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No versions yet.
          </Typography>
        )}
        {history.map((row, i) => (
          <React.Fragment key={row.weight_version}>
            {i > 0 && <Divider />}
            <ListItem sx={{ display: 'block', py: 1.5 }} disableGutters>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700 }}>v{row.weight_version}</Typography>
                <Chip label={row.set_by} size="small" color={setByColor(row.set_by)} />
                {row.is_active && <Chip label="Active" size="small" color="success" />}
                <Typography variant="caption" color="text.secondary">
                  {row.cell_count} cells
                </Typography>
                {row.created_at && (
                  <Typography variant="caption" color="text.secondary">
                    · {new Date(row.created_at).toLocaleString()}
                  </Typography>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  size="small"
                  startIcon={<VisibilityIcon fontSize="small" />}
                  onClick={() => viewHistorical(row.weight_version)}
                >
                  View
                </Button>
                {row.set_by === 'learned' && (
                  <Button size="small" onClick={() => checkPromotion(row.weight_version)}>
                    Check promotion
                  </Button>
                )}
                {!row.is_active && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onActivate(row.weight_version)}
                  >
                    Activate
                  </Button>
                )}
              </Box>
              {/* F10: the operator's rationale for this version (migration
                  572). Absent for versions written before the column existed
                  and for machine-written ('learned') ones — rendered as an
                  explicit "no note recorded" rather than blank space, so the
                  reader can tell "nobody said why" from "the panel forgot to
                  show it". */}
              <Typography
                variant="caption"
                data-testid={`weight-version-note-${row.weight_version}`}
                sx={{
                  display: 'block',
                  mt: 0.5,
                  color: row.edit_note ? 'text.secondary' : 'text.disabled',
                  fontStyle: row.edit_note ? 'normal' : 'italic',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {row.edit_note || 'No note recorded'}
              </Typography>
              {(loadingVersion === row.weight_version ||
                promotionStatus[row.weight_version]) && (
                <PromotionStatusPanel
                  status={promotionStatus[row.weight_version]}
                  loading={loadingVersion === row.weight_version}
                />
              )}
            </ListItem>
          </React.Fragment>
        ))}
      </List>

      <Dialog open={viewVersion !== null} onClose={closeViewer} maxWidth="lg" fullWidth>
        <DialogTitle>
          Viewing v{viewVersion} (historical)
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Read-only snapshot of this version's matrix. Nothing here changes the active version —
            use Activate on the row to make a version live.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {viewError && <Alert severity="error">{viewError}</Alert>}
          {!viewLoading && !viewError && viewMatrix && (
            <WeightMatrixGrid matrix={viewMatrix} edits={{}} onCellChange={NO_OP} disabled />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeViewer}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default VersionHistoryPanel;
